import { Controller } from "@hotwired/stimulus"
import Chart from "chart.js/auto"
import "chartjs-adapter-date-fns"

export default class extends Controller {
  static targets = ["chart", "metricCard", "benchmarkButton", "benchmarkResults", "refreshButton"]
  static values = { 
    refreshInterval: { type: Number, default: 30000 },
    autoRefresh: { type: Boolean, default: true }
  }

  connect() {
    this.initializeCharts()
    this.loadMetricsData()
    
    if (this.autoRefreshValue) {
      this.startAutoRefresh()
    }

    // Add event listeners for benchmark buttons
    this.benchmarkButtonTargets.forEach(button => {
      button.addEventListener('click', (e) => this.runBenchmark(e))
    })

    // Add refresh button listener
    if (this.hasRefreshButtonTarget) {
      this.refreshButtonTarget.addEventListener('click', () => this.refreshMetrics())
    }
  }

  disconnect() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer)
    }
  }

  initializeCharts() {
    this.charts = {}
    
    // Initialize Chart.js for each chart target
    this.chartTargets.forEach(chartElement => {
      const category = chartElement.dataset.category
      const metricName = chartElement.dataset.metricName
      
      const ctx = chartElement.getContext('2d')
      this.charts[`${category}_${metricName}`] = new Chart(ctx, {
        type: 'line',
        data: {
          datasets: [{
            label: this.formatMetricLabel(category, metricName),
            data: [],
            borderColor: this.getMetricColor(category),
            backgroundColor: this.getMetricColor(category, true),
            tension: 0.4,
            fill: true,
            pointRadius: 3,
            pointHoverRadius: 6
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              mode: 'index',
              intersect: false,
              callbacks: {
                label: (context) => {
                  return `${context.dataset.label}: ${this.formatValue(context.parsed.y, category)}`
                },
                title: (context) => {
                  return new Date(context[0].parsed.x).toLocaleTimeString()
                }
              }
            }
          },
          scales: {
            x: {
              type: 'time',
              time: {
                unit: 'minute',
                displayFormats: {
                  minute: 'HH:mm'
                }
              },
              grid: {
                display: true,
                color: 'rgba(0, 0, 0, 0.05)'
              }
            },
            y: {
              beginAtZero: true,
              grid: {
                display: true,
                color: 'rgba(0, 0, 0, 0.05)'
              },
              ticks: {
                callback: (value) => this.formatValue(value, category)
              }
            }
          },
          interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false
          }
        }
      })
    })
  }

  loadMetricsData() {
    fetch('/admin/performance.json')
      .then(response => response.json())
      .then(data => {
        this.updateMetricCards(data.latest_metrics)
        this.updateSummaryData(data.summary)
        this.loadChartData()
      })
      .catch(error => {
        console.error('Failed to load metrics data:', error)
        this.showError('Failed to load performance metrics')
      })
  }

  loadChartData() {
    this.chartTargets.forEach(chartElement => {
      const category = chartElement.dataset.category
      const metricName = chartElement.dataset.metricName
      
      fetch(`/admin/performance/metrics_data?category=${category}&metric_name=${metricName}&hours=6`)
        .then(response => response.json())
        .then(data => {
          const chart = this.charts[`${category}_${metricName}`]
          if (chart && data.data) {
            chart.data.datasets[0].data = data.data
            chart.update('none')
          }
        })
        .catch(error => {
          console.error(`Failed to load chart data for ${category}:`, error)
        })
    })
  }

  updateMetricCards(metrics) {
    this.metricCardTargets.forEach(card => {
      const category = card.dataset.category
      const metric = metrics[category]
      
      if (metric) {
        const valueElement = card.querySelector('[data-metric="value"]')
        const statusElement = card.querySelector('[data-metric="status"]')
        const timestampElement = card.querySelector('[data-metric="timestamp"]')
        
        if (valueElement) valueElement.textContent = metric.value
        if (statusElement) {
          statusElement.textContent = metric.health_status.charAt(0).toUpperCase() + metric.health_status.slice(1)
          statusElement.className = `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${this.getStatusClasses(metric.health_status)}`
        }
        if (timestampElement) {
          timestampElement.textContent = new Date(metric.timestamp).toLocaleTimeString()
        }

        // Update card background color based on status
        card.className = card.className.replace(/bg-\w+-50 border-\w+-200/g, '') + ' ' + this.getCardClasses(metric.health_status)
      }
    })
  }

  updateSummaryData(summary) {
    // Update any summary display elements if they exist
    Object.entries(summary).forEach(([category, data]) => {
      const summaryElement = document.querySelector(`[data-summary="${category}"]`)
      if (summaryElement) {
        const trendElement = summaryElement.querySelector('[data-trend]')
        if (trendElement) {
          trendElement.textContent = data.trend
          trendElement.className = `inline-flex items-center ${this.getTrendClasses(data.trend)}`
        }
      }
    })
  }

  runBenchmark(event) {
    const button = event.target
    const benchmarkType = button.dataset.benchmark
    const originalText = button.textContent
    
    // Disable button and show loading state
    button.disabled = true
    button.textContent = 'Running...'
    button.classList.add('opacity-50', 'cursor-not-allowed')

    fetch('/admin/performance/run_benchmark', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({ benchmark_type: benchmarkType })
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        this.showBenchmarkResults(data.results, benchmarkType)
        this.showSuccess(`${benchmarkType.replace('_', ' ').toUpperCase()} benchmark completed successfully`)
        
        // Refresh metrics after benchmark
        setTimeout(() => this.loadMetricsData(), 1000)
      } else {
        this.showError(`Benchmark failed: ${data.error}`)
      }
    })
    .catch(error => {
      console.error('Benchmark failed:', error)
      this.showError('Failed to run benchmark')
    })
    .finally(() => {
      // Re-enable button
      button.disabled = false
      button.textContent = originalText
      button.classList.remove('opacity-50', 'cursor-not-allowed')
    })
  }

  showBenchmarkResults(results, benchmarkType) {
    if (this.hasBenchmarkResultsTarget) {
      const resultsHtml = this.formatBenchmarkResults(results, benchmarkType)
      this.benchmarkResultsTarget.innerHTML = resultsHtml
      this.benchmarkResultsTarget.classList.remove('hidden')
    }
  }

  formatBenchmarkResults(results, benchmarkType) {
    let html = `<div class="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <h4 class="font-medium text-green-800 mb-2">${benchmarkType.replace('_', ' ').toUpperCase()} Results</h4>`
    
    if (benchmarkType === 'token_operations') {
      html += `
        <div class="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span class="text-green-600 font-medium">Generation:</span> 
            ${results.generation.ops_per_second.toFixed(1)} ops/sec
          </div>
          <div>
            <span class="text-green-600 font-medium">Verification:</span> 
            ${results.verification.ops_per_second.toFixed(1)} ops/sec
          </div>
        </div>`
    } else if (benchmarkType === 'full_suite') {
      html += '<div class="text-sm space-y-2">'
      Object.entries(results).forEach(([test, result]) => {
        if (result.success) {
          html += `<div class="text-green-600">✓ ${test.replace('_', ' ')}: Success</div>`
        } else {
          html += `<div class="text-red-600">✗ ${test.replace('_', ' ')}: Failed</div>`
        }
      })
      html += '</div>'
    } else {
      html += `<pre class="text-xs text-green-700 whitespace-pre-wrap">${JSON.stringify(results, null, 2)}</pre>`
    }
    
    html += '</div>'
    return html
  }

  refreshMetrics() {
    const button = this.refreshButtonTarget
    const originalText = button.textContent
    
    button.disabled = true
    button.textContent = 'Refreshing...'
    button.classList.add('opacity-50')
    
    this.loadMetricsData()
    
    setTimeout(() => {
      button.disabled = false
      button.textContent = originalText
      button.classList.remove('opacity-50')
    }, 1000)
  }

  startAutoRefresh() {
    this.refreshTimer = setInterval(() => {
      this.loadMetricsData()
    }, this.refreshIntervalValue)
  }

  // Utility methods
  formatMetricLabel(category, metricName) {
    return category.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  getMetricColor(category, withAlpha = false) {
    const colors = {
      token_generation: withAlpha ? 'rgba(34, 197, 94, 0.1)' : 'rgb(34, 197, 94)',
      token_verification: withAlpha ? 'rgba(59, 130, 246, 0.1)' : 'rgb(59, 130, 246)',
      database_operations: withAlpha ? 'rgba(168, 85, 247, 0.1)' : 'rgb(168, 85, 247)',
      rate_limiting: withAlpha ? 'rgba(245, 158, 11, 0.1)' : 'rgb(245, 158, 11)',
      security_operations: withAlpha ? 'rgba(239, 68, 68, 0.1)' : 'rgb(239, 68, 68)',
      memory_usage: withAlpha ? 'rgba(14, 165, 233, 0.1)' : 'rgb(14, 165, 233)',
      concurrent_operations: withAlpha ? 'rgba(139, 69, 19, 0.1)' : 'rgb(139, 69, 19)',
      stress_test: withAlpha ? 'rgba(75, 85, 99, 0.1)' : 'rgb(75, 85, 99)'
    }
    return colors[category] || (withAlpha ? 'rgba(107, 114, 128, 0.1)' : 'rgb(107, 114, 128)')
  }

  formatValue(value, category) {
    if (category.includes('operations') && category !== 'database_operations') {
      return `${value.toFixed(1)}%`
    } else if (category === 'database_operations') {
      return `${value.toFixed(1)} q/op`
    } else if (category === 'memory_usage') {
      return `${value.toFixed(2)} MB`
    } else {
      return `${value.toFixed(1)}`
    }
  }

  getStatusClasses(status) {
    switch (status) {
      case 'excellent':
        return 'bg-green-100 text-green-800'
      case 'good':
        return 'bg-yellow-100 text-yellow-800'
      case 'poor':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  getCardClasses(status) {
    switch (status) {
      case 'excellent':
        return 'bg-green-50 border-green-200'
      case 'good':
        return 'bg-yellow-50 border-yellow-200'
      case 'poor':
        return 'bg-red-50 border-red-200'
      default:
        return 'bg-gray-50 border-gray-200'
    }
  }

  getTrendClasses(trend) {
    switch (trend) {
      case 'improving':
        return 'text-green-600'
      case 'declining':
        return 'text-red-600'
      default:
        return 'text-gray-600'
    }
  }

  showSuccess(message) {
    this.showNotification(message, 'success')
  }

  showError(message) {
    this.showNotification(message, 'error')
  }

  showNotification(message, type) {
    // Create or update notification element
    const notification = document.createElement('div')
    notification.className = `fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${type === 'success' ? 'bg-green-100 text-green-800 border border-green-200' : 'bg-red-100 text-red-800 border border-red-200'}`
    notification.textContent = message
    
    document.body.appendChild(notification)
    
    setTimeout(() => {
      notification.remove()
    }, 5000)
  }
}